import { Dot, Flex, FlexProps, Text, variables } from '@bitrise/bitkit';
import {
  getStackedSeries,
  SeriesPointXY0,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGRects,
} from '@bitrise/bitviz';
import { TypeMetricKey, TypeMetricPercentile } from '@trace/types';
import React, { memo, PointerEvent, useCallback, useContext, useEffect, useState } from 'react';

import { METRIC_UNIT_BY_KEY } from '../../../../constants/Metrics';
import { HookResult } from '../../../Performance/usePercentileMetric';
import useTimeSeriesAxis from '../../../Performance/useTimeSeriesAxis';
import getFormattedMillisFromInterval from '../../../../utils/metric/getFormattedMillisFromInterval';
import getFormattedValue from '../../../../utils/metric/getFormattedValue';
import getTimeSeries from '../../../../utils/metric/getTimeSeries';
import { AttributesContext } from '../../../Attributes/Attributes';
import { colorScale3, scaleZero } from '../../colorScales';
import MessageFailing from '../../MessageFailing';
import MessageFetching from '../../MessageFetching';
import transition from '../../transition';
import StackBarTooltip from './StackedBarTooltip';

export interface StackedBarChartProps
  extends FlexProps,
    Pick<HookResult, 'metrics' | 'isError' | 'isFetching'> {
  colorScale?: (v: number) => string;
  height: number;
  isFetched: boolean;
  metric: TypeMetricKey;
  percentile?: TypeMetricPercentile;
}

type FocusPoint = [null | SeriesPointXY0, (null | number)[]];

function StackedBarChart(props: StackedBarChartProps) {
  const {
    colorScale = colorScale3,
    isError,
    isFetched,
    isFetching,
    height,
    refetch,
    metric,
    metrics,
    percentile = 'p95',
    ...rest
  } = props;
  const [[series, yDomain], setSeries] = useState<[SeriesPointXY0[][], [] | number[]]>([[], []]);
  const [[focusPoint, focusPointValues], setFocusPoint] = useState<FocusPoint>([null, []]);

  const { getAttributeValuesForMetric } = useContext(AttributesContext);
  const attributeValues = getAttributeValuesForMetric(metric);
  const xDomain = useTimeSeriesAxis();

  const handleSetFocusX = useCallback(
    (_: PointerEvent, point?: SeriesPointXY0) => {
      if (point && point.x !== null) {
        const index = xDomain.indexOf(point.x);

        if (index >= 0) {
          const values = attributeValues.map((value) =>
            metrics[value] ? metrics[value].views.percentile[percentile][index] : 0,
          );
          const s = series[series.length - 1];

          if (s && s[index]) {
            setFocusPoint([
              {
                x: point.x,
                y: s[index].y,
                y0: yDomain[0],
              },
              values,
            ]);

            return;
          }
        }
      }

      if (focusPoint) {
        setFocusPoint([null, []]);
      }
    },
    [attributeValues, focusPoint, metrics, percentile, series, xDomain, yDomain],
  );

  useEffect(() => {
    const [newSeries, newYDomain] = getTimeSeries(
      attributeValues.map((value) =>
        metrics[value] ? metrics[value].views.percentile[percentile] : [],
      ),
      xDomain,
    );

    setSeries(
      getStackedSeries(newSeries, {
        axis: 'y',
        domain: newYDomain,
      }),
    );
  }, [attributeValues, metrics, percentile, xDomain]);

  useEffect(() => {
    if ((isError || isFetching) && isFetched) {
      setSeries(([currentSeries, currentYDomain]) => [
        currentSeries.map((s) =>
          s.map(({ x }) => ({
            x,
            y: currentYDomain[0],
          })),
        ),
        currentYDomain,
      ]);
    }
  }, [isError, isFetching, isFetched]);

  return (
    <Flex {...rest}>
      <Flex container margin="x2">
        <Viz
          height={height}
          onPointerLeave={handleSetFocusX}
          onPointerMove={handleSetFocusX}
          paddingBottom={24}
          paddingLeft={32}
          paddingRight={16}
          paddingTop={8}
          transition={transition}
          xDomain={xDomain}
          xDomainPaddingInner={0.1}
          xDomainPaddingOuter={0.1}
          xDomainScale="band"
          yDomain={yDomain}
        >
          <VizSVG>
            {series
              .map((thisSeries, index, { length }) => (
                <VizSVGRects
                  animate
                  fill={colorScale(scaleZero(index, length))}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  maxWidth={16}
                  minHeight={index ? 0 : 2}
                  offsetHeight={-1}
                  opacity={isError || isFetching ? 0 : 1}
                  series={thisSeries}
                />
              ))
              .reverse()}

            <VizSVGAxisX
              animate={isFetched}
              fill={variables.colorGray5}
              format={(x) =>
                getFormattedMillisFromInterval(xDomain[0], xDomain[xDomain.length - 1], x)
              }
              stroke={variables.colorGray5}
              tickValues={[xDomain[0], xDomain[xDomain.length - 1]]}
            />

            <VizSVGAxisY
              animate={isFetched}
              fill={variables.colorGray5}
              flip
              format={(y) => getFormattedValue(METRIC_UNIT_BY_KEY[metric], y)}
              stroke={variables.colorGray5}
              tickValues={yDomain}
            />
          </VizSVG>

          <VizHTML>
            <VizHTMLArtboard>
              <MessageFailing failing={isError} onFetch={refetch} />
              <MessageFetching fetching={isFetching} />
            </VizHTMLArtboard>
          </VizHTML>

          <StackBarTooltip
            colorScale={colorScale}
            names={attributeValues}
            point={focusPoint}
            unit={METRIC_UNIT_BY_KEY[metric]}
            values={focusPointValues}
            visible={!!focusPoint && !isError && !isFetching}
            xLower={xDomain[0]}
            xUpper={xDomain[xDomain.length - 1]}
          />
        </Viz>
      </Flex>

      <Flex
        alignChildrenHorizontal="middle"
        alignChildrenVertical="middle"
        direction="horizontal"
        gap="x6"
        wrap
      >
        {attributeValues.map((value, index) => (
          <Flex alignChildrenVertical="baseline" direction="horizontal" gap="x1" key={value}>
            <Flex>
              <Dot
                size="10px"
                style={{ backgroundColor: colorScale(scaleZero(index, attributeValues.length)) }}
              />
            </Flex>

            <Flex>
              <Text config="8" textColor="gray-6">
                {value}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

export default memo(StackedBarChart);
